import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import cookie from "react-cookies";

import { ApiCallResult, useAPI } from "services/Api";
import Page from "pages/Page";
import { navigate } from "utility/NavigationService";
import { renderLoader } from "utility/LoaderUtils";
import { useNotificationManager } from "notifications/NotificationManager";
import { ModularHeaderConfig } from "./ModularHeader";

const PatientProfile = ({
    children,
    parentComponentName = null,
    shouldRedirect = false,
    // Callbacks
    onPatientData = null
}) => {
    const componentName = `${parentComponentName || "[PatientProfile]"}`;

    const [coach, setCoach] = useState(null);
    const [employers, setEmployers] = useState(null);
    const [employerAdmins, setEmployerAdmins] = useState(null);
    const [facilitator, setFacilitator] = useState(null);
    const [headerConfig, setHeaderConfig] = useState<ModularHeaderConfig>(null);
    const [insurerAdmins, setInsurerAdmins] = useState(null);
    const [insurers, setInsurers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [nurseCaseManagers, setNurseCaseManagers] = useState(null);
    const [nurseCaseManagerAdmins, setNurseCaseManagerAdmins] = useState(null);
    const [patientData, setPatientData] = useState(null);
    const [physician, setPhysician] = useState(null);
    const [provider, setProvider] = useState(null);
    const [tpas, setTpas] = useState(null);
    const [tpaAdmins, setTpaAdmins] = useState(null);
    const [utilizationReviewers, setUtilizationReviewers] = useState(null);
    const [utilizationReviewerAdmins, setUtilizationReviewerAdmins] = useState(null);
    const [vetKitVersion, setVetKitVersion] = useState(null);
    const patientId = useParams().id;

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const role = cookie.load("role") ? cookie.load("role") : "None";

    // ---=== Utilities ===--- //

    const editPatient = () => {
        if (patientId) {
            void navigate(`/patient/${patientId}/edit`);
        }
    };

    const goToEditCoachingPlan = () => {
        void navigate(`/patient/${patientId}/coaching-plan`);
    };

    const gotToPatientActivity = () => {
        void navigate(`/patient/${patientId}/usage`);
    };

    const toReports = () => {
        void navigate(`/patient/${patientId}/reports`);
    };

    const redirect = () => {
        if (shouldRedirect) {
            let roleType = role.toLowerCase();
            if (roleType === "coach") {
                void navigate(`/patient/${patientId}/coaching-plan`);
            }
            else if (roleType === "facilitator") {
                void navigate(`/patient/${patientId}/reports`);
            }
            else {
                void navigate(`/reportsExternal`);
            }
        }
    };

    const getPatientHeight = () => {
        let feet = patientData.profile.height * 3.2808;
        let inches = Math.round((
            feet - Math.floor(feet)) * 12);
        if (inches >= 12) {
            inches -= 12;
            feet += 1;
        }
        return `${Math.floor(feet)}' ${inches}"`;
    };

    const getGender = (gender: number) => {
        switch (gender) {
            case 0:
                return "Female";
            case 1:
                return "Male";
            default:
                return "Nonbinary";
        }
    };

    const getPatientData = async() => {
        const result: ApiCallResult = await API.api_Get(
            componentName,
            `/patients/${patientId}`,
            notificationManager,
            (response) => {
                if (!response) {
                    throw new Error("No response from server");
                }

                let data = response.data;
                setPatientData(data);

                if (data.client?.version) {
                    let val = data.client.version.split("+unitycloudbuild")[0];
                    if (val.includes("prod")) {
                        val = val.split("-")[0];
                    }
                    setVetKitVersion(val);
                }

                setProvider(data.provider_name ? data.provider_name : "None");
                setPhysician(data.physician_name ? data.physician_name : "None");
                setCoach(data.coach_name ? data.coach_name : "None");
                setFacilitator(data.facilitator_name ? data.facilitator_name : "None");

                let profile = data.profile;
                if (profile) {
                    const handleField = (field: string, setter: (value: string | null) => void) => {
                        const value = profile[field];
                        if (!value) {
                            setter(null);
                            return;
                        }

                        if (typeof value === "string") {
                            setter(value);
                        }
                        else if (Array.isArray(value) && value.length > 0) {
                            setter(value.join(", "));
                        }
                        else {
                            setter(null);
                        }
                    };

                    handleField("insurers", setInsurers);
                    handleField("insurer_admins", setInsurerAdmins);
                    handleField("third_party_administrators", setTpas);
                    handleField("third_party_administrator_admins", setTpaAdmins);
                    handleField("utilization_reviewers", setUtilizationReviewers);
                    handleField("utilization_reviewer_admins", setUtilizationReviewerAdmins);
                    handleField("nurse_case_managers", setNurseCaseManagers);
                    handleField("nurse_case_manager_admins", setNurseCaseManagerAdmins);
                    handleField("employers", setEmployers);
                    handleField("employer_admins", setEmployerAdmins);
                }

                onPatientData?.(data);
            },
            (error) => console.error(error)
        );

        setLoading(!(result.wasSuccessful && result.wasCallable));
    };

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            if (patientId === null) {
                void navigate("/");
                return;
            }

            if (shouldRedirect) {
                redirect();
                return;
            }

            if (patientData === null) {
                void getPatientData();
            }
        }, []
    );

    useEffect(
        () => {
            if (!patientData) {
                return;
            }

            let tabs = [];
            let roleType = role.toLowerCase();
            if (roleType === "staff" || roleType === "coach" || roleType === "facilitator") {
                tabs.push({
                    key: "coaching-plan",
                    text: "Coaching Plan",
                    click: goToEditCoachingPlan,
                    current: window.location.href.includes("coaching-plan")
                });
            }
            tabs.push({
                key: "activity",
                text: "Activity",
                click: gotToPatientActivity,
                current: window.location.href.includes("usage")
            });
            if (roleType === "staff" || roleType === "coach" || roleType === "facilitator") {
                tabs.push({
                    key: "reports",
                    text: "Reports",
                    click: toReports,
                    current: window.location.href.includes("reports")
                });
            }
            setHeaderConfig({
                trail: breadCrumbLinks(),
                body: {
                    subheader:
                        `#${patientId} | ${getGender(patientData.profile.gender)} | ${getPatientHeight()}`,
                    name:
                        patientData.first_name + " " + patientData.last_name,
                    vetKitVersion: vetKitVersion,
                    info: {
                        provider: provider,
                        physician: physician,
                        coach: coach,
                        employers: employers,
                        employerAdmins: employerAdmins,
                        facilitator: facilitator,
                        insurers: insurers,
                        insurerAdmins: insurerAdmins,
                        nurseCaseManagers: nurseCaseManagers,
                        nurseCaseManagerAdmins: nurseCaseManagerAdmins,
                        tpas: tpas,
                        tpaAdmins: tpaAdmins,
                        utilizationReviewers: utilizationReviewers,
                        utilizationReviewerAdmins: utilizationReviewerAdmins
                    },
                    tabs: tabs,
                    sideButton: {
                        img: "/icons/edit.svg",
                        title: "Edit Patient",
                        click: editPatient
                    }
                },
                cindra: { enabled: true },
                loading: false // Set to false when data is loaded
            });
        }, [patientData]
    );

    // ---=== Render ===--- //

    const getModularHeaderConfig = (): ModularHeaderConfig => {
        return {
            trail: breadCrumbLinks(),
            cindra: { enabled: true },
            loading: true // Pass loading state when no patient data
        };
    };

    const breadCrumbLinks = () => {
        return [
            { label: "Patients", url: "/" }
        ];
    };

    return (
        <Page
            componentName={componentName}
            headerConfig={headerConfig ? headerConfig : getModularHeaderConfig()}
            internal={true}
        >
            {
                loading
                    ? (
                        renderLoader("Loading Patient Data")
                    )
                    : (
                        <div className="w-full h-full flex flex-col">
                            {/* Shadow line spacer to delineate breadcrumbs from content */}
                            <div className="w-full h-px bg-gray-200 shadow-sm"></div>
                            <div className={`w-full flex-1 justify-center items-start inline-flex whitespace-nowrap max-[1200px]:overflow-auto overflow-y-auto`}>
                                {children}
                            </div>
                        </div>
                    )
            }
        </Page>
    );
};

export default PatientProfile;